import { useQuery, UseQueryOptions } from 'react-query';
import { ApiQueryKey } from '@higo/api/lib/apiQueryKey';
import { fetchPatientHistory } from '@higo/api/lib/services/patientService';
import {
  mapToPatientHistoryResponse,
  mapToPatientVisit,
  PaginatedListReqDTO,
  PatientHistoryResponse,
  mapToPatientExaminations,
} from '@higo/api/lib/models';
import { isNil } from 'rambda';
import { useDoctorPortalApiContext } from 'contexts/ApiContext';

export const usePatientHistoryQuery = (
  patientId: number,
  pageData?: PaginatedListReqDTO,
  options?: Omit<
    UseQueryOptions<
      PatientHistoryResponse,
      unknown,
      PatientHistoryResponse,
      (PaginatedListReqDTO | ApiQueryKey | number | undefined)[]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { authorizedAxios } = useDoctorPortalApiContext();

  return useQuery(
    [ApiQueryKey.PatientHistory, patientId, pageData],
    async () => {
      const dto = await fetchPatientHistory(authorizedAxios)(patientId, pageData);

      if ((dto as any)?.content && Array.isArray((dto as any).content)) {
        const content: any[] = (dto as any).content;

        const examinationsRaw = content.filter((it) => it.recordType !== 'VISIT');
        const visitsRaw = content.filter((it) => it.recordType === 'VISIT');

        const examinations = examinationsRaw
          .map((e) => {
            const normalized = {
              ...e,
              examinationId:
                e.examinationId ?? e.id ?? e.examId ?? e.examination?.id,
            } as any;

            try {
              return mapToPatientExaminations(normalized as any);
            } catch (err) {
              return undefined as any;
            }
          })
          .filter((x) => x && typeof x.examinationId === 'number');

        const visits = visitsRaw.map((v) => {
          const visitDto = {
            ...v,
            executedBy: v.doctor ?? v.executedBy ?? null,
            id: v.id ?? v.visitId,
            createDate: v.createDate,
            status: v.status,
            patientId: v.patientId,
          } as any;

          return mapToPatientVisit(visitDto as any);
        });

        return {
          examinations,
          visits,
        } as PatientHistoryResponse;
      }

      return mapToPatientHistoryResponse(dto as any);
    },
    { enabled: !isNil(patientId), keepPreviousData: true, ...options },
  );
};
